import { createEffect, createMemo, Show } from "solid-js"
import {
  Reactions,
  ReactionsFragment,
} from "@/designSystem/components/reactions"
import { TableOfContents } from "@/designSystem/components/table-of-contents"
import { HumanContentPill } from "@/designSystem/patterns/human-content-badge"
import {
  type NavPost,
  PostNavigation,
} from "@/designSystem/patterns/post-navigation"
import { PostPreview } from "@/designSystem/patterns/post-preview"
import { graphql, type ResultOf } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  applyHeadingIds,
  extractHeadingsFromContent,
} from "@/lib/heading-processor"
import {
  ContentAccessBadge,
  ContentAccessBadgeFragment,
} from "@/pages/subscriptions/content-access-badge"
import { PostComments } from "@/pages/subscriptions/post-comments"
import { contentAreaClass } from "@/pages/subscriptions/subscription-item.css"
import { PrivateContentPrompt } from "@/pages/subscriptions/subscription-item-private-content-prompt"
import { UpgradeModal } from "@/pages/subscriptions/subscription-item-upgrade-modal"

export const PublishedPostFragment = graphql(
  `fragment PublishedPostView on PublishedPost {
    id
    title
    description
    content
    contentAccess {
      hasAccess
      reason
      restricted
      requiredTiers {
        id
        name
        monthlyPriceCents
        annualPriceCents
      }
    }
    ...ContentAccessBadge
    imageUrl
    url
    firstPublishedAt
    featuredAt
    humanContent
    publication {
      id
      name
      url
      enableMonetization
      subscriptionTiers {
        id
        name
        description
        monthlyPriceCents
        annualPriceCents
        canPurchase
      }
      sub {
        id
        tier {
          id
          name
        }
      }
      author {
        id
        username
        authorName
        profileImage
      }
    }
    commentsEnabled
    ...ReactionsFragment
  }`,
  [ContentAccessBadgeFragment, ReactionsFragment],
)

export type PublishedPost = ResultOf<typeof PublishedPostFragment>

export function PublishedPostView(props: {
  post: PublishedPost
  cursor: string | null
  previousPost: NavPost | null
  nextPost: NavPost | null
  executeQuery: (opts?: { requestPolicy?: "network-only" }) => void
  showUpgradeModal: boolean
  setShowUpgradeModal: (open: boolean) => void
  previousLabel: string
  nextLabel: string
  buildPostUrl: (post: NavPost) => string
}) {
  const { LL } = useI18nContext()

  const hasAccess = createMemo(() => props.post.contentAccess.hasAccess)

  const tocHeadings = createMemo(() => {
    if (!hasAccess()) return []
    return extractHeadingsFromContent(props.post.content)
  })

  const showToc = createMemo(() => tocHeadings().length >= 3)

  function handleContentRef(el: HTMLDivElement) {
    createEffect(() => {
      const headings = tocHeadings()
      if (headings.length > 0) {
        applyHeadingIds(el, headings)
      }
    })
  }

  function handleUpgradeSuccess() {
    props.executeQuery({ requestPolicy: "network-only" })
  }

  return (
    <>
      <PostPreview
        post={{
          title: props.post.title,
          description: props.post.description,
          imageUrl: props.post.imageUrl,
          date: props.post.firstPublishedAt ?? new Date(),
          content: props.post.content ?? "",
          authorName:
            props.post.publication.author.authorName ??
            props.post.publication.name,
          authorProfileImage: props.post.publication.author.profileImage,
          authorProfileUrl: props.post.publication.author.username
            ? `${props.post.publication.url}/about/${props.post.publication.author.username}`
            : null,
        }}
        truncated={!hasAccess()}
        subscribed={!!props.post.publication.sub}
        subscribedLabel={LL().subscriptions.following()}
        subscribedTitle={LL().subscribe.defaultText({
          userName:
            props.post.publication.author.authorName ??
            props.post.publication.name,
        })}
        headerExtra={
          <>
            <ContentAccessBadge post={props.post} />
            <Show when={props.post.humanContent}>
              <HumanContentPill as="span" />
            </Show>
          </>
        }
        afterHeader={
          <Show when={showToc()}>
            <TableOfContents
              headings={tocHeadings()}
              label={LL().subscriptions.tableOfContents()}
              variant="inline"
            />
          </Show>
        }
        contentAreaClass={showToc() ? contentAreaClass : undefined}
        contentAreaExtra={
          <Show when={showToc()}>
            <TableOfContents
              headings={tocHeadings()}
              label={LL().subscriptions.tableOfContents()}
              variant="desktop"
            />
          </Show>
        }
        contentRef={handleContentRef}
      >
        <Show when={!hasAccess()}>
          <PrivateContentPrompt
            reason={
              props.post.contentAccess.reason as
                | "not_logged_in"
                | "not_subscribed"
                | "wrong_tier"
                | null
            }
            requiredTiers={props.post.contentAccess.requiredTiers}
            onUpgradeClick={() => {
              props.setShowUpgradeModal(true)
            }}
          />
        </Show>
        <Show when={hasAccess()}>
          <Reactions post={props.post} />
        </Show>
        <Show when={hasAccess() && props.post.commentsEnabled !== false}>
          <PostComments postId={props.post.id} />
        </Show>
      </PostPreview>

      <UpgradeModal
        open={props.showUpgradeModal}
        onOpenChange={props.setShowUpgradeModal}
        requiredTierIds={props.post.contentAccess.requiredTiers.map(t => t.id)}
        publication={props.post.publication}
        onSuccess={handleUpgradeSuccess}
      />
      <Show when={props.cursor}>
        <PostNavigation
          previousPost={props.previousPost}
          nextPost={props.nextPost}
          previousLabel={props.previousLabel}
          nextLabel={props.nextLabel}
          buildPostUrl={props.buildPostUrl}
        />
      </Show>
    </>
  )
}
